import { History, Route, Save, Search, Users } from "lucide-react";
import type { Responsibility } from "./settings-types";
import { confidenceLabel } from "./route-evidence";
import type { usePreparationSettingsWorkspace } from "./usePreparationSettingsWorkspace";
export function MaterialPreparationSettingsView({workspace}: {workspace: ReturnType<typeof usePreparationSettingsWorkspace>}) {
const {
  reference,
  warehouses,
  warehouseUsers,
  selectedUserId,
  setSelectedUserId,
  assignments,
  assignmentWarehouseCode,
  setAssignmentWarehouseCode,
  responsibility,
  setResponsibility,
  assignmentsLoading,
  assignmentWorking,
  assignmentError,
  lineId,
  setLineId,
  productCode,
  setProductCode,
  stagingWarehouseCode,
  setStagingWarehouseCode,
  mapping,
  candidate,
  evidence,
  note,
  setNote,
  loading,
  working,
  error,
  productOptions,
  suggest,
  selectCandidate,
  save,
  responsibilityOptions,
  saveAssignment,
} = workspace;
return (
    <>
      <section
        className="settings-card material-route-settings"
        aria-labelledby="material-route-title"
      >
        <div className="settings-title">
          <Route size={18} />
          <div>
            <h2 id="material-route-title">Üretim malzeme depo rotası</h2>
            <p>
              Reçete malzemelerinin Mikro transfer geçmişini inceleyin; öneriyi
              doğrulamadan hat eşleştirmesine dönüştürmeyin.
            </p>
          </div>
        </div>

        {loading ? <p role="status">Hat ve depo kataloğu okunuyor…</p> : null}
        {error ? (
          <p className="field-error" role="alert">
            {error}
          </p>
        ) : null}

        {!loading && reference ? (
          <>
            <label>
              Üretim hattı
              <select
                value={lineId}
                onChange={(event) => setLineId(event.target.value)}
              >
                {reference.lines.map((line) => (
                  <option key={line.id} value={line.id}>
                    {line.code} · {line.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Kanıt ürünü
              <input
                list="material-route-products"
                value={productCode}
                onChange={(event) => setProductCode(event.target.value)}
                maxLength={80}
                placeholder="Ürün kodu"
              />
              <datalist id="material-route-products">
                {productOptions.map((order) => (
                  <option key={order.code} value={order.code}>
                    {order.name}
                  </option>
                ))}
              </datalist>
            </label>
            <label>
              Hedef cep depo
              <select
                value={stagingWarehouseCode}
                onChange={(event) =>
                  setStagingWarehouseCode(event.target.value)
                }
              >
                <option value="">Depo seçin</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.code} value={warehouse.code}>
                    {warehouse.code} · {warehouse.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="secondary-button"
              onClick={suggest}
              disabled={working}
            >
              <Search size={15} />{" "}
              {working ? "Geçmiş okunuyor…" : "Geçmiş hareketlerden rota bul"}
            </button>

            {mapping ? (
              <div className="material-route-current">
                <History size={18} />
                <div>
                  <strong>
                    Kayıtlı rota: {mapping.mainWarehouseCode} →{" "}
                    {mapping.handoffWarehouseCode} →{" "}
                    {mapping.stagingWarehouseCode}
                  </strong>
                  <small>
                    Sürüm {mapping.version} · {mapping.note}
                  </small>
                </div>
              </div>
            ) : null}

            {evidence?.candidates.map((item) => (
              <button
                type="button"
                className={`material-route-candidate${candidate === item ? " is-selected" : ""}`}
                key={`${item.mainWarehouse.code}-${item.handoffWarehouse.code}`}
                onClick={() => selectCandidate(item)}
                aria-pressed={candidate === item}
              >
                <Route size={18} />
                <div>
                  <strong>
                    {item.mainWarehouse.code} → {item.handoffWarehouse.code} →{" "}
                    {item.stagingWarehouse.code} ·{" "}
                    {confidenceLabel[item.confidence]}
                  </strong>
                  <small>
                    {item.mainWarehouse.name} → {item.handoffWarehouse.name} →{" "}
                    {item.stagingWarehouse.name}
                  </small>
                  <small>
                    {item.mainToHandoffMovementCount} +{" "}
                    {item.handoffToStagingMovementCount} hareket · reçetedeki{" "}
                    {item.recipeMaterialCount} malzemeden iki bacakta{" "}
                    {Math.min(
                      item.mainToHandoffDistinctMaterialCount,
                      item.handoffToStagingDistinctMaterialCount,
                    )}{" "}
                    tanesi görüldü
                  </small>
                </div>
              </button>
            ))}

            {evidence?.candidates.length ? (
              <small role="status">
                Kanıt kaynağı Mikro ERP · son {evidence.lookbackDays} gün ·{" "}
                {new Intl.DateTimeFormat("tr-TR", {
                  dateStyle: "short",
                  timeStyle: "short",
                }).format(new Date(evidence.observedAt))}
              </small>
            ) : null}

            {candidate ? (
              <>
                <label>
                  Doğrulama gerekçesi
                  <textarea
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    minLength={10}
                    maxLength={500}
                    rows={4}
                  />
                </label>
                <button
                  type="button"
                  className="primary-button"
                  onClick={save}
                  disabled={working}
                >
                  <Save size={15} /> Doğrulanmış rotayı kaydet
                </button>
              </>
            ) : null}
          </>
        ) : null}
      </section>

      <section
        className="settings-card material-route-settings"
        aria-labelledby="warehouse-responsibility-title"
      >
        <div className="settings-title">
          <Users size={18} />
          <div>
            <h2 id="warehouse-responsibility-title">
              Depo kullanıcı sorumlulukları
            </h2>
            <p>
              Depo kullanıcılarının yalnız görevli oldukları Mikro depolarını
              görmesini ve işlem yapmasını sağlayın.
            </p>
          </div>
        </div>
        {assignmentError ? (
          <p className="field-error" role="alert">
            {assignmentError}
          </p>
        ) : null}
        {warehouseUsers.length ? (
          <>
            <label>
              Kullanıcı
              <select
                value={selectedUserId}
                onChange={(event) => setSelectedUserId(event.target.value)}
              >
                {warehouseUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Sorumluluk türü
              <select
                value={responsibility}
                onChange={(event) =>
                  setResponsibility(event.target.value as Responsibility)
                }
              >
                {responsibilityOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Mikro depo
              <select
                value={assignmentWarehouseCode}
                onChange={(event) =>
                  setAssignmentWarehouseCode(event.target.value)
                }
              >
                <option value="">Depo seçin</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.code} value={warehouse.code}>
                    {warehouse.code} · {warehouse.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="primary-button"
              disabled={assignmentWorking || !assignmentWarehouseCode}
              onClick={() => void saveAssignment(true)}
            >
              <Save size={15} /> Sorumluluk ekle
            </button>

            {assignmentsLoading ? (
              <p role="status">Depo sorumlulukları okunuyor…</p>
            ) : assignments.length ? (
              <div className="material-warehouse-assignments">
                {[...assignments]
                  .sort((left, right) =>
                    `${left.warehouseCode}:${left.responsibility}`.localeCompare(
                      `${right.warehouseCode}:${right.responsibility}`,
                    ),
                  )
                  .map((assignment) => {
                    const warehouse = warehouses.find(
                      (item) => item.code === assignment.warehouseCode,
                    );
                    const responsibilityLabel =
                      assignment.responsibility === "MAIN"
                        ? "Ana depo"
                        : assignment.responsibility === "HANDOFF"
                          ? "Teslim / tesellüm"
                          : "Cep depo";
                    return (
                      <div
                        className="material-route-current material-warehouse-assignment"
                        key={`${assignment.warehouseCode}-${assignment.responsibility}`}
                      >
                        <Users size={17} />
                        <div>
                          <strong>
                            {assignment.warehouseCode} ·{" "}
                            {warehouse?.name ?? "Mikro depo"} ·{" "}
                            {responsibilityLabel}
                          </strong>
                          <small>
                            {assignment.active ? "Etkin" : "Pasif"} · sürüm{" "}
                            {assignment.version} · {assignment.assignedBy} ·{" "}
                            {new Intl.DateTimeFormat("tr-TR", {
                              dateStyle: "short",
                              timeStyle: "short",
                            }).format(new Date(assignment.assignedAt))}
                          </small>
                        </div>
                        <button
                          type="button"
                          className="secondary-button"
                          disabled={assignmentWorking}
                          onClick={() =>
                            void saveAssignment(!assignment.active, assignment)
                          }
                        >
                          {assignment.active ? "Pasifleştir" : "Etkinleştir"}
                        </button>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p role="status">
                Bu kullanıcı için depo sorumluluğu tanımlanmamış.
              </p>
            )}
          </>
        ) : (
          <p role="status">
            Aktif depo görevlisi veya cep depo sorumlusu bulunmuyor. Önce
            Kullanıcılar bölümünden ilgili rolü atayın.
          </p>
        )}
      </section>
    </>
  );
}
